import React, { useState, useEffect } from 'react';
import { CATEGORY_OPTIONS } from '../utils/categoryConfig';
import { createPost, updatePost, uploadImage } from '../api/feedApi';
import './CreatePostModal.css';

const CreatePostModal = ({ isOpen, onClose, onSuccess, editPost = null }) => {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [content, setContent] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (editPost) {
      setTitle(editPost.title);
      setCategory(editPost.category);
      setContent(editPost.content);
      setImageUrl(editPost.imageUrl || '');
      setImagePreview(editPost.imageUrl || '');
    } else {
      resetForm();
    }
  }, [editPost, isOpen]);

  const resetForm = () => {
    setTitle('');
    setCategory('');
    setContent('');
    setImageFile(null);
    setImagePreview('');
    setImageUrl('');
    setError('');
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }

      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
    setImageUrl('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (!category && !editPost) {
      setError('Category is required');
      return;
    }

    if (!content.trim()) {
      setError('Content is required');
      return;
    }

    setLoading(true);

    try {
      let finalImageUrl = imageUrl;

      // Upload image if new file selected
      if (imageFile) {
        setUploading(true);
        const uploadResponse = await uploadImage(imageFile);
        finalImageUrl = uploadResponse.data.imageUrl;
        setUploading(false);
      }

      const postData = {
        title: title.trim(),
        content: content.trim(),
        imageUrl: finalImageUrl || undefined
      };

      if (!editPost) {
        postData.category = category;
        await createPost(postData);
      } else {
        await updatePost(editPost.id, postData);
      }

      resetForm();
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save post');
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="create-post-modal-overlay" onClick={onClose}>
      <div className="create-post-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="create-post-modal-header">
          <h2>{editPost ? 'Edit Post' : 'Create New Post'}</h2>
          <button className="create-post-modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="create-post-form">
          {error && <div className="create-post-form-error">{error}</div>}

          <div className="create-post-form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter post title..."
              maxLength={255}
              required
            />
            <span className="create-post-char-count">{title.length}/255</span>
          </div>

          {!editPost && (
            <div className="create-post-form-group">
              <label htmlFor="category">Category *</label>
              <select
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                required
              >
                <option value="">Select a category</option>
                {CATEGORY_OPTIONS.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.icon} {cat.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="create-post-form-group">
            <label htmlFor="content">Content *</label>
            <textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Share your thoughts..."
              rows={8}
              required
            />
          </div>

          <div className="create-post-form-group">
            <label>Image (optional)</label>
            {!imagePreview ? (
              <div className="create-post-image-upload">
                <input
                  type="file"
                  id="image"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
                <label htmlFor="image" className="create-post-upload-label">
                  <span className="create-post-upload-icon">📷</span>
                  <span>Choose an image</span>
                  <span className="create-post-upload-hint">JPG, PNG, GIF, WEBP (max 5MB)</span>
                </label>
              </div>
            ) : (
              <div className="create-post-image-preview">
                <img src={imagePreview} alt="Preview" />
                <button
                  type="button"
                  className="create-post-remove-image"
                  onClick={handleRemoveImage}
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          <div className="create-post-modal-footer">
            <button
              type="button"
              className="create-post-btn create-post-btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="create-post-btn create-post-btn-primary"
              disabled={loading || uploading}
            >
              {uploading ? 'Uploading...' : loading ? 'Saving...' : editPost ? 'Update Post' : 'Create Post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePostModal;
